/**
 * HRMS — Tenant Control Centre
 * Client-side entry point (webpack bundle)
 *
 * Auth architecture: Firebase Identity Platform (GCIP) multi-tenancy.
 * Before signIn the caller must set auth.tenantId to the GCIP tenant ID
 * for the org being accessed (or null for super-admin / system pool access).
 * Use HRMGMT.resolveTenant({ emailDomain }) to look up the GCIP tenant ID
 * from an email address before presenting the password field.
 *
 * Initialises Firebase, then exposes a single `window.HRMGMT` façade whose
 * methods map 1-to-1 with every Cloud Function in functions/index.js.
 * All calls go through httpsCallable — no privileged Admin SDK code runs here.
 *
 * Argument convention matches portal.js call sites (positional, not a single
 * payload object) so no changes to portal.js are required.
 */

// ─────────────────────────────────────────────────────────────
// IMPORTS — Firebase modular SDK (tree-shaken by webpack)
// ─────────────────────────────────────────────────────────────

import { initializeApp }                        from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
}                                               from "firebase/auth";
import {
  getFunctions,
  httpsCallable,
  // connectFunctionsEmulator,   // ← uncomment for local emulator
}                                               from "firebase/functions";
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
}                                               from "firebase/storage";

// ─────────────────────────────────────────────────────────────
// CSS assets bundled by webpack
// ─────────────────────────────────────────────────────────────

import "./css/all.min.css";

// ─────────────────────────────────────────────────────────────
// PORTAL UI — must be imported here so webpack bundles it.
// portal.js wires all DOM handlers inside its own DOMContentLoaded
// listener and reads window.HRMGMT, which bootstrap() sets first.
// ─────────────────────────────────────────────────────────────

import "./js/portal.js";

// ─────────────────────────────────────────────────────────────
// FIREBASE CONFIGURATION
// ─────────────────────────────────────────────────────────────

const firebaseConfig = {
  apiKey: "AIzaSyDNmVhN2Us4euvbMCR7YqAdefdzadZudqc",
  authDomain: "png-hr.firebaseapp.com",
  projectId: "png-hr",
  storageBucket: "png-hr.firebasestorage.app",
  messagingSenderId: "682188257394",
  appId: "1:682188257394:web:47559507aba19c94532aeb"
};

// ─────────────────────────────────────────────────────────────
// FIREBASE INIT
// ─────────────────────────────────────────────────────────────

const app       = initializeApp(firebaseConfig);
const auth      = getAuth(app);
const functions = getFunctions(app, "us-central1");
const storage   = getStorage(app);

// connectFunctionsEmulator(functions, "localhost", 5001); // dev emulator

// ─────────────────────────────────────────────────────────────
// INTERNAL HELPER
// Wraps httpsCallable: auto-unwraps result.data and converts
// FirebaseFunctionsError into a plain Error with .code attached.
// ─────────────────────────────────────────────────────────────

function cf(name) {
  const fn = httpsCallable(functions, name);
  return async (payload) => {
    try {
      const result = await fn(payload ?? {});
      return result.data;
    } catch (err) {
      const wrapped   = new Error(err?.message || `"${name}" failed.`);
      wrapped.code    = err?.code    ?? "unknown";
      wrapped.details = err?.details ?? null;
      throw wrapped;
    }
  };
}

// ─────────────────────────────────────────────────────────────
// RAW CALLABLE REFS
// One httpsCallable per Cloud Function, keyed by function name.
// ─────────────────────────────────────────────────────────────

const _fn = {
  debugAuth: cf('debugAuth'),
  // module definitions
  publishModuleDefinitions: cf('publishModuleDefinitions'),
  saveModuleDefinition:   cf('saveModuleDefinition'),
  deleteModuleDefinition: cf('deleteModuleDefinition'),
  listModuleDefinitions:  cf('listModuleDefinitions'),
  getModuleDefinition:    cf('getModuleDefinition'),
  createTenant:           cf("createTenant"),
  getTenant:              cf("getTenant"),
  listTenants:            cf("listTenants"),
  saveTenantConfig:       cf("saveTenantConfig"),
  suspendTenant:          cf("suspendTenant"),
  reactivateTenant:       cf("reactivateTenant"),
  startSupportSession:    cf("startSupportSession"),
  endSupportSession:      cf("endSupportSession"),
  changeTenantPlan:       cf("changeTenantPlan"),
  deleteTenant:           cf("deleteTenant"),
  toggleTenantModule:     cf("toggleTenantModule"),
  setAllModules:          cf("setAllModules"),
  resetTenantModulesToPlan: cf("resetTenantModulesToPlan"),
  applyBulkPreset:        cf("applyBulkPreset"),
  getGlobalRules:         cf("getGlobalRules"),
  saveGlobalRules:        cf("saveGlobalRules"),
  getActivityLog:         cf("getActivityLog"),
  getDashboardMetrics:    cf("getDashboardMetrics"),
  getTenantMetrics:       cf("getTenantMetrics"),
  listPlans:              cf("listPlans"),
  getPlan:                cf("getPlan"),
  savePlan:               cf("savePlan"),
  setSuperAdminClaim:     cf("setSuperAdminClaim"),
  // GCIP-native role management
  setTenantUserRole:      cf("setTenantUserRole"),
  setUserRole:            cf("setUserRole"),
  // Tenant resolution for login flow
  resolveTenant:          cf("resolveTenant"),
  getQuotaAlerts:         cf("getQuotaAlerts"),
  getTenantOnboarding:    cf("getTenantOnboarding"),
  saveTenantOnboarding:   cf("saveTenantOnboarding"),
  getBillingInfo:         cf("getBillingInfo"),
  saveBillingInfo:        cf("saveBillingInfo"),
  generateInvoice:        cf("generateInvoice"),
  generateBatchInvoices:  cf("generateBatchInvoices"),
  listInvoices:           cf("listInvoices"),
  updateInvoiceStatus:    cf("updateInvoiceStatus"),
  createSupportTicket:    cf("createSupportTicket"),
  listSupportTickets:     cf("listSupportTickets"),
  updateTicketStatus:     cf("updateTicketStatus"),
  getTenantAuditLog:      cf("getTenantAuditLog"),
  writeAuditLog:          cf("writeAuditLog"),
  getTenantUsageStats:    cf("getTenantUsageStats"),
  // Access rules (sub-collection)
  saveModuleAccessRules:  cf("saveModuleAccessRules"),
  getModuleAccessRules:   cf("getModuleAccessRules"),
  // Custom roles
  createCustomRole:       cf("createCustomRole"),
  getCustomRoles:         cf("getCustomRoles"),
  updateCustomRole:       cf("updateCustomRole"),
  deleteCustomRole:       cf("deleteCustomRole"),
  // Messaging service
  getMessagingService:          cf("getMessagingService"),
  saveMessagingService:         cf("saveMessagingService"),
  getEffectiveMessagingLimits:  cf("getEffectiveMessagingLimits"),
  getTenantMessagingConfig:     cf("getTenantMessagingConfig"),
  saveTenantMessagingConfig:    cf("saveTenantMessagingConfig"),
  // Tenant user management
  listTenantUsers:              cf("listTenantUsers"),
  listAllTenantUsers:           cf("listAllTenantUsers"),
  setTenantUserStatus:          cf("setTenantUserStatus"),
  // Onboarding event tracking
  recordOnboardingEvent:        cf("recordOnboardingEvent"),
  notifyAdminFirstLogin:        cf("notifyAdminFirstLogin"),
  // Password reset (standalone — also fires automatically inside createTenant)
  sendTenantPasswordResetEmail: cf("sendTenantPasswordResetEmail"),
  provisionTenantAdmin:         cf("provisionTenantAdmin"),
  // Tenant reset
  resetTenant:                  cf("resetTenant"),
  purgeTenantData:              cf("purgeTenantData"),
  // Billing metrics
  getBillingMetrics:            cf("getBillingMetrics"),
  // Platform reference data
  listRefData:                  cf("listRefData"),
  saveRefDataEntry:             cf("saveRefDataEntry"),
  deleteRefDataEntry:           cf("deleteRefDataEntry"),
  bulkImportRefData:            cf("bulkImportRefData"),
  // Module schema config (compliance validation layer)
  saveModuleSchema:             cf("saveModuleSchema"),
  getModuleSchema:              cf("getModuleSchema"),
  listModuleSchemas:            cf("listModuleSchemas"),
  // System health & setup
  systemHealthCheck:      cf("systemHealthCheck"),
  runSetupWizard:         cf("runSetupWizard"),
  getFileUploadConfig:    cf("getFileUploadConfig"),
  saveFileUploadConfig:   cf("saveFileUploadConfig"),
  saveProvinceGeoJSON:    cf("saveProvinceGeoJSON"),
  getProvinceGeoJSON:     cf("getProvinceGeoJSON"),
  // Tenant employee data import (calls HR backend adminBatchImport)
  adminBatchImport:       cf("adminBatchImport"),
  // Period year management (reads/writes db-hr via admin SDK)
  getTenantPeriodYears:   cf("adminGetTenantPeriodYears"),
  initTenantPeriodYears:  cf("adminInitTenantPeriodYears"),
  // Platform PS Reference Library
  listPlatformPSRefItems:  cf("listPlatformPSRefItems"),
  savePlatformPSRefItem:   cf("savePlatformPSRefItem"),
  deletePlatformPSRefItem: cf("deletePlatformPSRefItem"),
  seedPlatformPSRefItems:  cf("seedPlatformPSRefItems"),
  // Global AI Knowledge Base (super-admin managed; fed into all tenant AI assistants)
  listGlobalAiDocs:   cf("listGlobalAiDocs"),
  getGlobalAiDoc:     cf("getGlobalAiDoc"),
  saveGlobalAiDoc:    cf("saveGlobalAiDoc"),
  deleteGlobalAiDoc:  cf("deleteGlobalAiDoc"),
  // Platform email templates (HR backend emailTemplates.js; super-admin only)
  listEmailTemplates:       cf("listEmailTemplates"),
  getEmailTemplate:         cf("getEmailTemplate"),
  saveEmailTemplate:        cf("saveEmailTemplate"),
  resetEmailTemplate:       cf("resetEmailTemplate"),
  uploadEmailTemplateImage: cf("uploadEmailTemplateImage"),
  previewEmailTemplate:     cf("previewEmailTemplate"),
  sendTestEmailTemplate:    cf("sendTestEmailTemplate"),
};

// ─────────────────────────────────────────────────────────────
// HRMGMT FAÇADE
// Argument signatures match portal.js call sites exactly.
// ─────────────────────────────────────────────────────────────

const HRMGMT = {
  debugAuth() {
    return _fn.debugAuth();
  },

  // ── Auth ──────────────────────────────────────────────────

  /**
   * resolveTenant({ emailDomain?, code? })
   * Looks up the GCIP tenant ID for an organisation before sign-in.
   * Call this first, then pass gcipTenantId to signIn().
   * Returns { gcipTenantId, tenantId, tenantName }.
   */
  resolveTenant(payload) {
    return _fn.resolveTenant(payload);
  },

  /**
   * signIn(email, password, gcipTenantId)
   *
   * GCIP multi-tenancy: auth.tenantId must be set before calling
   * signInWithEmailAndPassword so the issued JWT carries the correct
   * firebase.tenant claim.
   *
   *   gcipTenantId = null  → system pool (super-admin login)
   *   gcipTenantId = "..."  → org tenant login
   *
   * Typical portal flow:
   *   1. User enters email
   *   2. Call HRMGMT.resolveTenant({ emailDomain }) → gcipTenantId
   *   3. Call HRMGMT.signIn(email, password, gcipTenantId)
   */
  signIn(email, password, gcipTenantId = null) {
    // Set tenant context before sign-in; null = system/super-admin pool
    auth.tenantId = gcipTenantId;
    return signInWithEmailAndPassword(auth, email, password);
  },

  /** Sign the current user out and reset the tenant context. */
  signOut() {
    auth.tenantId = null;
    return signOut(auth);
  },

  /** Subscribe to auth state changes. Matches firebase onAuthStateChanged. */
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },

  /** Return the currently signed-in user, or null. */
  getCurrentUser() {
    return auth.currentUser;
  },

  //- Modules ───────────────────────────────────────────────────

  // In the HRMGMT facade object, add:
  saveModuleDefinition(payload)    { return _fn.saveModuleDefinition(payload); },
  deleteModuleDefinition(moduleId) { return _fn.deleteModuleDefinition({ moduleId }); },
  listModuleDefinitions()          { return _fn.listModuleDefinitions(); },
  getModuleDefinition(moduleId)    { return _fn.getModuleDefinition({ moduleId }); },

  // ── Tenants ───────────────────────────────────────────────

  /** createTenant(payload) */
  createTenant(payload) {
    return _fn.createTenant(payload);
  },

  /** getTenant(tenantId) */
  getTenant(tenantId) {
    return _fn.getTenant({ tenantId });
  },

  /** listTenants(status?) */
  listTenants(status) {
    return _fn.listTenants(status ? { status } : {});
  },

  /**
   * saveTenantConfig(tenantId, { adminEmail?, employeeLimit?, notes? })
   * portal.js: HRMGMT.saveTenantConfig(id, { adminEmail, employeeLimit, notes })
   */
  saveTenantConfig(tenantId, fields) {
    return _fn.saveTenantConfig({ tenantId, ...fields });
  },

  /** suspendTenant(tenantId) */
  suspendTenant(tenantId) {
    return _fn.suspendTenant({ tenantId });
  },

  /** reactivateTenant(tenantId) */
  reactivateTenant(tenantId) {
    return _fn.reactivateTenant({ tenantId });
  },

  /**
   * changeTenantPlan(tenantId, plan, resetModules?)
   * portal.js: HRMGMT.changeTenantPlan(id, newPlan, resetMods)
   */
  changeTenantPlan(tenantId, plan, resetModules = false) {
    return _fn.changeTenantPlan({ tenantId, plan, resetModules });
  },

  /** deleteTenant(tenantId) */
  deleteTenant(tenantId) {
    return _fn.deleteTenant({ tenantId });
  },

  /**
   * startSupportSession(tenantId)
   * Mints a one-time custom token that opens the tenant's HR portal signed
   * in as a dedicated "HR Support" account (full_admin rights), so a
   * super-admin can assist/act on the tenant's behalf. Returns
   * { customToken, gcipTenantId, expiresAt }.
   */
  startSupportSession(tenantId) {
    return _fn.startSupportSession({ tenantId });
  },

  /** endSupportSession(tenantId) — revoke an active support session. */
  endSupportSession(tenantId) {
    return _fn.endSupportSession({ tenantId });
  },

  /**
   * resetTenant(tenantId, adminPayload)
   * Wipes a tenant back to a brand-new state. Removes all users, resets
   * Firestore doc to factory defaults, creates a fresh admin account and
   * sends a password-reset email.
   *
   * adminPayload: { adminEmail, adminFirstName, adminLastName,
   *                 adminPhone?, adminDob?, adminSex? }
   */
  resetTenant(tenantId, adminPayload) {
    return _fn.resetTenant({ tenantId, ...adminPayload });
  },

  /**
   * purgeTenantData(tenantId, confirmCode)
   * Wipes ALL HR data, auth users, and sub-collections. Resets tenant
   * to a clean "pending" state so they can re-join as brand new.
   */
  purgeTenantData(tenantId, confirmCode) {
    return _fn.purgeTenantData({ tenantId, confirmCode });
  },

  // ── Modules ───────────────────────────────────────────────

  /**
   * toggleTenantModule(tenantId, moduleId, enabled)
   * portal.js: HRMGMT.toggleTenantModule(tenantId, moduleId, enabled)
   */
  toggleTenantModule(tenantId, moduleId, enabled, featureId) {
    return _fn.toggleTenantModule({ tenantId, moduleId, enabled, ...(featureId ? {featureId} : {}) });
  },

  /**
   * setAllModules(tenantId, enabled)
   * portal.js: HRMGMT.setAllModules(tenantId, enabled)
   */
  setAllModules(tenantId, enabled) {
    return _fn.setAllModules({ tenantId, enabled });
  },

  /** resetTenantModulesToPlan(tenantId) */
  resetTenantModulesToPlan(tenantId) {
    return _fn.resetTenantModulesToPlan({ tenantId });
  },

  /**
   * applyBulkPreset(tenantId, preset)
   * portal.js: HRMGMT.applyBulkPreset(tid, preset)
   */
  applyBulkPreset(tenantId, preset) {
    return _fn.applyBulkPreset({ tenantId, preset });
  },

  // ── Global Rules ──────────────────────────────────────────

  /** getGlobalRules() */
  getGlobalRules() {
    return _fn.getGlobalRules();
  },

  /**
   * saveGlobalRules(rulesObject)
   * portal.js: HRMGMT.saveGlobalRules(state.globalRules)
   *            HRMGMT.saveGlobalRules({ allowedDomains: [...] })
   */
  saveGlobalRules(rules) {
    return _fn.saveGlobalRules(rules);
  },

  // ── Activity Log ──────────────────────────────────────────

  /**
   * getActivityLog(opts?)
   * portal.js: HRMGMT.getActivityLog({ limit, type?, startAfter? })
   */
  getActivityLog(opts) {
    return _fn.getActivityLog(opts ?? {});
  },

  // ── Metrics ───────────────────────────────────────────────

  /** getDashboardMetrics() */
  getDashboardMetrics() {
    return _fn.getDashboardMetrics();
  },

  /** getTenantMetrics(tenantId) */
  getTenantMetrics(tenantId) {
    return _fn.getTenantMetrics({ tenantId });
  },

  // ── Plans ─────────────────────────────────────────────────

  /** listPlans() */
  listPlans() {
    return _fn.listPlans();
  },

  /** getPlan(planId) */
  getPlan(planId) {
    return _fn.getPlan({ planId });
  },

  /**
   * savePlan(planId, planData)
   * portal.js: HRMGMT.savePlan(planId, { label, employeeLimit, storageLimitMb, modules, color })
   */
  savePlan(planId, planData) {
    return _fn.savePlan({ planId, ...planData });
  },

  // ── Auth Utilities ────────────────────────────────────────

  /**
   * setSuperAdminClaim(targetUid, value)
   * portal.js: HRMGMT.setSuperAdminClaim(uid, grant)
   */
  setSuperAdminClaim(targetUid, value) {
    return _fn.setSuperAdminClaim({ targetUid, value });
  },

  // ── New: Quota, Onboarding, Billing, Tickets, Audit ─────────

  getQuotaAlerts(threshold = 80) {
    return _fn.getQuotaAlerts({ threshold });
  },

  getTenantOnboarding(tenantId) {
    return _fn.getTenantOnboarding({ tenantId });
  },

  saveTenantOnboarding(tenantId, fields) {
    return _fn.saveTenantOnboarding({ tenantId, ...fields });
  },

  getBillingInfo(tenantId) {
    return _fn.getBillingInfo({ tenantId });
  },

  saveBillingInfo(tenantId, fields) {
    return _fn.saveBillingInfo({ tenantId, ...fields });
  },

  /** generateInvoice(payload) */
  generateInvoice(payload) {
    return _fn.generateInvoice(payload);
  },

  /** generateBatchInvoices(payload) */
  generateBatchInvoices(payload) {
    return _fn.generateBatchInvoices(payload);
  },

  /** listInvoices(payload) */
  listInvoices(payload) {
    return _fn.listInvoices(payload || {});
  },

  /** updateInvoiceStatus(payload) */
  updateInvoiceStatus(payload) {
    return _fn.updateInvoiceStatus(payload);
  },

  createSupportTicket(tenantId, subject, description, priority = "medium") {
    return _fn.createSupportTicket({ tenantId, subject, description, priority });
  },

  listSupportTickets(opts) {
    return _fn.listSupportTickets(opts ?? {});
  },

  updateTicketStatus(ticketId, status, assignedTo, resolution) {
    return _fn.updateTicketStatus({ ticketId, status, assignedTo, resolution });
  },

  getTenantAuditLog(tenantId, opts) {
    return _fn.getTenantAuditLog({ tenantId, ...(opts ?? {}) });
  },

  writeAuditLog(action, before, after, meta) {
    return _fn.writeAuditLog({ action, before: before ?? null, after: after ?? null, meta: meta ?? {} });
  },

  getTenantUsageStats(tenantId, days = 30) {
    return _fn.getTenantUsageStats({ tenantId, days });
  },

  getTenantPeriodYears(tenantId) {
    return _fn.getTenantPeriodYears({ tenantId });
  },

  initTenantPeriodYears(tenantId) {
    return _fn.initTenantPeriodYears({ tenantId });
  },

  // ── Access Rules (sub-collection) ─────────────────────────

  /** saveModuleAccessRules(tenantId, rules) */
  saveModuleAccessRules(tenantId, rules) {
    return _fn.saveModuleAccessRules({ tenantId, rules });
  },

  /** getModuleAccessRules(tenantId, moduleId?) */
  getModuleAccessRules(tenantId, moduleId) {
    return _fn.getModuleAccessRules({ tenantId, ...(moduleId ? { moduleId } : {}) });
  },

  // ── Custom Roles ──────────────────────────────────────────

  /** createCustomRole(tenantId, payload) */
  createCustomRole(tenantId, payload) {
    return _fn.createCustomRole({ tenantId, ...payload });
  },

  /** getCustomRoles(tenantId) */
  getCustomRoles(tenantId) {
    return _fn.getCustomRoles({ tenantId });
  },

  /** updateCustomRole(tenantId, roleId, fields) */
  updateCustomRole(tenantId, roleId, fields) {
    return _fn.updateCustomRole({ tenantId, roleId, ...fields });
  },

  /** deleteCustomRole(tenantId, roleId) */
  deleteCustomRole(tenantId, roleId) {
    return _fn.deleteCustomRole({ tenantId, roleId });
  },

  // ── Messaging Service ─────────────────────────────────────

  /** getMessagingService() */
  getMessagingService() {
    return _fn.getMessagingService();
  },

  /** saveMessagingService(payload) */
  saveMessagingService(payload) {
    return _fn.saveMessagingService(payload);
  },

  /** getEffectiveMessagingLimits(tenantId) */
  getEffectiveMessagingLimits(tenantId) {
    return _fn.getEffectiveMessagingLimits({ tenantId });
  },

  /** getTenantMessagingConfig(tenantId) */
  getTenantMessagingConfig(tenantId) {
    return _fn.getTenantMessagingConfig({ tenantId });
  },

  /** saveTenantMessagingConfig(tenantId, payload) */
  saveTenantMessagingConfig(tenantId, payload) {
    return _fn.saveTenantMessagingConfig({ tenantId, ...payload });
  },

  // ── Role claim helpers ────────────────────────────────────

  /**
   * setTenantUserRole(targetUid, gcipTenantId, role?, customRoleId?)
   *
   * GCIP-native role assignment. Routes the update through the correct
   * TenantAwareAuth instance. userRole is the only claim written — tenant
   * membership is encoded in the GCIP JWT firebase.tenant field.
   *
   * portal.js: HRMGMT.setTenantUserRole(uid, gcipTenantId, role, customRoleId?)
   */
  setTenantUserRole(targetUid, gcipTenantId, role = "employee", customRoleId) {
    return _fn.setTenantUserRole({
      targetUid,
      gcipTenantId: gcipTenantId ?? null,
      role,
      ...(customRoleId ? {customRoleId} : {}),
    });
  },

  /**
   * setUserRole(targetUid, gcipTenantId, role, customRoleId?)
   * Updates userRole on a user within their GCIP tenant.
   * portal.js: HRMGMT.setUserRole(uid, gcipTenantId, role, customRoleId?)
   */
  setUserRole(targetUid, gcipTenantId, role, customRoleId) {
    return _fn.setUserRole({
      targetUid,
      gcipTenantId,
      role,
      ...(customRoleId ? {customRoleId} : {}),
    });
  },

  // ── Tenant user management ────────────────────────────────

  /**
   * listTenantUsers({ tenantId, role?, status?, limit? })
   * Returns all Firebase Auth users registered under a tenant.
   */
  listTenantUsers(payload) {
    return _fn.listTenantUsers(payload);
  },

  /**
   * listAllTenantUsers({ tenantId?, role?, status?, limit? })
   * Returns all users across all tenants (platform-level view).
   */
  listAllTenantUsers(payload) {
    return _fn.listAllTenantUsers(payload);
  },

  /**
   * setTenantUserStatus({ uid, gcipTenantId, disabled })
   * Enable or disable a tenant user's Firebase Auth account.
   * gcipTenantId is required to route the update to the correct GCIP tenant.
   */
  setTenantUserStatus(payload) {
    return _fn.setTenantUserStatus(payload);
  },

  // ── Onboarding tracking ───────────────────────────────────

  /**
   * recordOnboardingEvent({ tenantId, event, label, meta? })
   * Append a custom event to the tenant's onboarding timeline.
   */
  recordOnboardingEvent(payload) {
    return _fn.recordOnboardingEvent(payload);
  },

  // ── Password reset ────────────────────────────────────────

  /**
   * sendTenantPasswordResetEmail(tenantId, email)
   * Sends a password-reset / invite email to the tenant admin via Firebase's
   * Identity Toolkit REST API, scoped to the correct GCIP tenant.
   * Also fires automatically inside createTenant — use this for resends.
   */
  sendTenantPasswordResetEmail(tenantId, email) {
    return _fn.sendTenantPasswordResetEmail({ tenantId, email });
  },

  /**
   * provisionTenantAdmin(tenantId, email, profile?)
   * Creates the GCIP admin account if it doesn't exist, stamps full claims,
   * then sends the password-reset / invite email. Idempotent.
   *
   * profile: { firstName?, lastName?, phone?, dob?, sex? }
   * When profile is supplied the display name and db-hr user doc are populated.
   */
  provisionTenantAdmin(tenantId, email, profile = {}) {
    return _fn.provisionTenantAdmin({ tenantId, email, ...profile });
  },

  // ── Billing metrics ───────────────────────────────────────

  /**
   * getBillingMetrics({ months? })
   * Returns MRR, ARR, collection rate, trend by month, breakdown by plan.
   */
  getBillingMetrics(payload) {
    return _fn.getBillingMetrics(payload);
  },

  // ── Platform Reference Data ───────────────────────────────

  /**
   * listRefData({ moduleId, featureId, status?, limit? })
   * Returns all entries for a module/feature reference dataset.
   */
  listRefData(payload) {
    return _fn.listRefData(payload);
  },

  /**
   * saveRefDataEntry({ moduleId, featureId, entryId?, data })
   * Create or update a reference data entry.
   */
  saveRefDataEntry(payload) {
    return _fn.saveRefDataEntry(payload);
  },

  /**
   * deleteRefDataEntry({ moduleId, featureId, entryId })
   * Permanently delete a reference data entry.
   */
  deleteRefDataEntry(payload) {
    return _fn.deleteRefDataEntry(payload);
  },

  /**
   * bulkImportRefData({ moduleId, featureId, entries, mode })
   * Bulk import reference data entries (append or replace).
   */
  bulkImportRefData(payload) {
    return _fn.bulkImportRefData(payload);
  },

  /**
   * seedRefDataset({ moduleId, featureId })
   * Seeds a built-in platform dataset into platform/refdata from the backend.
   * No seed data is bundled in the frontend.
   */
  seedRefDataset(payload) {
    return _fn.seedRefDataset(payload);
  },

  /**
   * publishModuleDefinitions()
   * Syncs all module definitions from the admin portal into
   * platform/modules/{id} so client apps can read them at runtime.
   */
  publishModuleDefinitions(payload) {
    return _fn.publishModuleDefinitions(payload);
  },

  /**
   * getClientBootstrap({ tenantId })
   * Single call for the client app: returns module definitions,
   * tenant module config, access rules, and ref data manifest.
   */
  getClientBootstrap(payload) {
    return _fn.getClientBootstrap(payload);
  },

  // ── Module Schema Config ──────────────────────────────────

  /**
   * saveModuleSchema({ moduleId, schema })
   * Validates and deploys a module schema JSON to Firestore.
   * Writes to db-management/platform/moduleSchemas/{moduleId} (platform default)
   * and to db-hr/tenants/{tid}/moduleConfigs/{moduleId} for every active tenant.
   * portal.js: HRMGMT.saveModuleSchema({ moduleId, schema })
   */
  saveModuleSchema(payload) {
    return _fn.saveModuleSchema(payload);
  },

  /**
   * getModuleSchema({ moduleId })
   * Reads the platform-default schema for a module from Firestore.
   * Returns { moduleId, schema, savedAt, savedByEmail, schemaVersion, featureCount }
   * or { moduleId, schema: null } if not yet deployed.
   * portal.js: HRMGMT.getModuleSchema(moduleId)
   */
  getModuleSchema(moduleId) {
    return _fn.getModuleSchema({ moduleId });
  },

  /**
   * listModuleSchemas()
   * Returns a summary array of all module schemas saved to the platform.
   * Used by Module Studio init to restore schemaState without fetching full docs.
   * portal.js: HRMGMT.listModuleSchemas()
   */
  listModuleSchemas() {
    return _fn.listModuleSchemas();
  },

  // ── Platform PS Reference Library ─────────────────────────────
  listPlatformPSRefItems()          { return _fn.listPlatformPSRefItems(); },
  savePlatformPSRefItem(payload)    { return _fn.savePlatformPSRefItem(payload); },
  deletePlatformPSRefItem(itemId)   { return _fn.deletePlatformPSRefItem({itemId}); },
  seedPlatformPSRefItems(overwrite) { return _fn.seedPlatformPSRefItems({overwrite: !!overwrite}); },

  uploadPSRefPDF(file, onProgress) {
    return new Promise((resolve, reject) => {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `platform/ps-ref-library/${Date.now()}_${safeName}`;
      const ref  = storageRef(storage, path);
      const task = uploadBytesResumable(ref, file, { contentType: 'application/pdf' });
      task.on('state_changed',
        snap => { if (onProgress) onProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)); },
        err  => reject(err),
        async () => {
          const downloadURL = await getDownloadURL(task.snapshot.ref);
          resolve({ downloadURL, storagePath: path });
        }
      );
    });
  },

  deletePSRefPDF(storagePath) {
    if (!storagePath) return Promise.resolve();
    return deleteObject(storageRef(storage, storagePath));
  },

  // ── Tenant data import (HR backend) ──────────────────────────
  adminBatchImport(payload)     { return _fn.adminBatchImport(payload); },

  // ── System health & setup ─────────────────────────────────
  systemHealthCheck()           { return _fn.systemHealthCheck(); },
  runSetupWizard(payload)       { return _fn.runSetupWizard(payload); },
  getFileUploadConfig()         { return _fn.getFileUploadConfig(); },
  saveFileUploadConfig(payload) { return _fn.saveFileUploadConfig(payload); },
  saveProvinceGeoJSON(geojsonString) { return _fn.saveProvinceGeoJSON({ geojsonString }); },
  getProvinceGeoJSON()         { return _fn.getProvinceGeoJSON(); },

  // ── Global AI Knowledge Base ──────────────────────────────
  listGlobalAiDocs()           { return _fn.listGlobalAiDocs({}); },
  getGlobalAiDoc(docId)        { return _fn.getGlobalAiDoc({ docId }); },
  saveGlobalAiDoc(payload)     { return _fn.saveGlobalAiDoc(payload); },
  deleteGlobalAiDoc(docId)     { return _fn.deleteGlobalAiDoc({ docId }); },

  // ── Platform email templates ──────────────────────────────
  listEmailTemplates()                   { return _fn.listEmailTemplates({}); },
  getEmailTemplate(templateId)           { return _fn.getEmailTemplate({ templateId }); },
  saveEmailTemplate(templateId, fields)  { return _fn.saveEmailTemplate({ templateId, fields }); },
  resetEmailTemplate(templateId)         { return _fn.resetEmailTemplate({ templateId }); },
  uploadEmailTemplateImage(templateId, slot, data, contentType) {
    return _fn.uploadEmailTemplateImage({ templateId, slot, data, contentType });
  },
  previewEmailTemplate(templateId, fields) { return _fn.previewEmailTemplate({ templateId, fields }); },
  sendTestEmailTemplate(templateId, fields, to) {
    return _fn.sendTestEmailTemplate({ templateId, fields, to });
  },

  /**
   * Upload an AI knowledge base file to Firebase Storage.
   * Returns { downloadURL, storagePath }.
   * storagePath is then passed into saveGlobalAiDoc as-is.
   */
  uploadGlobalAiFile(file) {
    const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `aiDocs/_global/${Date.now()}_${safe}`;
    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef(storage, path), file, {
        contentType: file.type || "application/octet-stream",
      });
      task.on("state_changed", null,
        err => reject(err),
        async () => {
          const downloadURL = await getDownloadURL(task.snapshot.ref);
          resolve({ downloadURL, storagePath: path });
        }
      );
    });
  },

  deleteGlobalAiFile(storagePath) {
    if (!storagePath) return Promise.resolve();
    return deleteObject(storageRef(storage, storagePath));
  },
};

// ─────────────────────────────────────────────────────────────
// EXPOSE GLOBAL — set immediately so portal.js's DOMContentLoaded
// handler can safely reference window.HRMGMT whenever it fires.
// ─────────────────────────────────────────────────────────────

window.HRMGMT = HRMGMT;

// ─────────────────────────────────────────────────────────────
// NAMED EXPORTS (for any future ES-module consumers)
// ─────────────────────────────────────────────────────────────

export { HRMGMT, auth };
export default HRMGMT;